import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

interface TenantRouteProps {
  children: React.ReactNode;
  requiredPermissions?: string[];
  requiredFeatures?: string[];
  requiredRoles?: string[];
  fallbackPath?: string;
}

export function TenantRoute({ 
  children, 
  requiredPermissions = [],
  requiredFeatures = [],
  requiredRoles = [],
  fallbackPath = '/unauthorized'
}: TenantRouteProps) {
  const { 
    currentUser, 
    userProfile, 
    userPermissions, 
    currentTenant, 
    loading,
    hasPermission,
    hasRole,
    canAccessFeature
  } = useAuth();
  const [authorized, setAuthorized] = useState<boolean | null>(null);

  useEffect(() => {
    if (loading) return;

    const timeoutId = setTimeout(() => {
      if (!currentUser || !userProfile || !currentTenant) {
        setAuthorized(false);
        return;
      }

      // Check if user is active
      if (!userProfile.is_active) {
        setAuthorized(false);
        return;
      }

      // Check permissions
      const hasAllPermissions = requiredPermissions.every(permission =>
        hasPermission(permission)
      );

      // Check features
      const hasAllFeatures = requiredFeatures.every(feature =>
        canAccessFeature(feature)
      );

      // Check roles
      const hasRequiredRole = requiredRoles.length === 0 || requiredRoles.some(role =>
        hasRole(role)
      );

      setAuthorized(hasAllPermissions && hasAllFeatures && hasRequiredRole);
    }, 0)
    return () => clearTimeout(timeoutId)
  }, [
    loading,
    currentUser, 
    userProfile, 
    currentTenant, 
    userPermissions, 
    requiredPermissions, 
    requiredFeatures, 
    requiredRoles,
    hasPermission,
    hasRole,
    canAccessFeature
  ]);

  if (loading || authorized === null) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  if (!userProfile || !currentTenant) {
    return <Navigate to="/login" replace />;
  }

  if (!authorized) {
    return <Navigate to={fallbackPath} replace />;
  }
  
  return <>{children}</>;
}

// Convenience components for common permission patterns
export function RequirePermission({ 
  permission, 
  children, 
  fallbackPath = '/unauthorized' 
}: { 
  permission: string; 
  children: React.ReactNode; 
  fallbackPath?: string;
}) {
  return (
    <TenantRoute requiredPermissions={[permission]} fallbackPath={fallbackPath}>
      {children}
    </TenantRoute>
  );
}

export function RequireRole({ 
  role, 
  children, 
  fallbackPath = '/unauthorized' 
}: { 
  role: string; 
  children: React.ReactNode; 
  fallbackPath?: string;
}) {
  return (
    <TenantRoute requiredRoles={[role]} fallbackPath={fallbackPath}>
      {children}
    </TenantRoute>
  );
}

export function RequireFeature({ 
  feature, 
  children, 
  fallbackPath = '/unauthorized' 
}: { 
  feature: string; 
  children: React.ReactNode; 
  fallbackPath?: string;
}) {
  return (
    <TenantRoute requiredFeatures={[feature]} fallbackPath={fallbackPath}>
      {children}
    </TenantRoute>
  );
}
